import { Check, LogOut, Mail, Settings, User } from 'lucide-react';
import { useNavigate } from 'react-router';
import { paths } from '../app/paths';
import { useLogout, useMe } from '../auth/session';
import { useI18n } from '../i18n/i18n';
import { cn } from '../lib/cn';
import { useInbox, useMyWorkspaces } from '../workspaces/workspaces-api';
import { Avatar } from './ui/avatar';
import { Menu, MenuContent, MenuGroup, MenuItem, MenuSeparator, MenuTrigger } from './ui/menu';

/**
 * Иконка пользователя (инициалы) и меню: пространства, настройки, выход
 * (docs/saas/screens/блок-схемы.md). Точка на иконке — ждут ответа приглашения.
 */
export function UserMenu({ workspaceId, className }: { workspaceId?: string; className?: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const me = useMe();
  const workspaces = useMyWorkspaces();
  const inbox = useInbox();
  const logout = useLogout();
  const profile = me.data;
  if (profile === null || profile === undefined) return null;

  const waiting = inbox.data?.items.length ?? 0;
  const items = workspaces.data?.pages[0]?.items.filter((item) => !item.archived) ?? [];

  return (
    <Menu>
      <MenuTrigger>
        <button
          type="button"
          aria-label={t('menu.open')}
          title={profile.name}
          className={cn(
            'relative inline-flex rounded-full transition-[filter] outline-none hover:brightness-95 focus-visible:ring-2 focus-visible:ring-primary/40',
            className,
          )}
        >
          <Avatar name={profile.name} email={profile.email} src={profile.avatarUrl ?? null} />
          {waiting > 0 && (
            <span className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full border-2 border-background bg-primary" />
          )}
        </button>
      </MenuTrigger>
      <MenuContent align="end">
        {/* Как в прототипе: имя мелко серым, затем пространства и настройки (без выбора языка, ADR-0021). */}
        <div
          className="max-w-64 truncate px-2.5 py-1.5 text-xs text-muted-foreground"
          title={`${profile.name} · ${profile.email}`}
        >
          {profile.name}
        </div>
        <MenuSeparator />
        <MenuGroup label={t('menu.workspaces')}>
          {items.map((item) => (
            <MenuItem key={item.id} onClick={() => void navigate(paths.workspace(item.id))}>
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              {item.id === workspaceId && <Check className="size-3.5! text-primary" />}
            </MenuItem>
          ))}
          <MenuItem onClick={() => void navigate(paths.workspaces)}>
            {t('menu.allWorkspaces')}
          </MenuItem>
          {waiting > 0 && (
            <MenuItem onClick={() => void navigate(paths.workspaces)}>
              <Mail />
              <span className="flex-1">{t('menu.invitations')}</span>
              <span className="rounded-full bg-primary px-1.5 text-2xs text-control-foreground">
                {waiting}
              </span>
            </MenuItem>
          )}
        </MenuGroup>
        <MenuSeparator />
        {workspaceId !== undefined && (
          <MenuItem onClick={() => void navigate(paths.workspaceSettings(workspaceId))}>
            <Settings />
            {t('menu.workspaceSettings')}
          </MenuItem>
        )}
        <MenuItem onClick={() => void navigate(paths.profile)}>
          <User />
          {t('menu.profile')}
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          disabled={logout.isPending}
          onClick={() => logout.mutate(undefined, { onSettled: () => void navigate(paths.root) })}
        >
          <LogOut />
          {t('nav.logout')}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
